import { GetReportQueriesRequestProps } from "../infrastructure/reportQueries.api";
import { parseISO, subMonths } from "date-fns";
import { asUTCTime, currentDate } from "../../../lib/dateUtils";
import { TableColumns, TableFilters } from "../../shared/crud/list";
import { ReportQueriesEntity } from "../domain/reportQueries.entity";
import {
  BooleanRenderer,
  calculateDateFromProtoDate,
  calculateDateTimeFromProtoDate,
  DateRenderer,
  DateTimeRenderer,
  NumberRenderer,
  StringRenderer,
} from "../../shared/renderers/table";
import { useIntl } from "react-intl";
import { useMemo } from "react";
import { dateFilter } from "../../shared/crud/filter-widgets";
import { formatMessage } from "../../../lib/intlUtils";
import { reportQueriesMessages } from "../messages";

interface ReportQueriesFilters {
  period: string;
}

type ReportQueriesCell = Omit<
  ReportQueriesEntity,
  "date" | "checkIn" | "checkOut" | "hasAvailability" | "isChain"
> & {
  date: string;
  checkIn: string;
  checkOut: string;
  hasAvailability: string;
  isChain: string;
};

interface ReportQueriesCellCSV {
  message: { id: string; defaultMessage: string };
  value: (entity: ReportQueriesCell) => string;
}

const defaultRequest = {
  startDate: asUTCTime(subMonths(currentDate(), 1), "yyyy-MM-dd HH:mm:ss"),
  endDate: asUTCTime(currentDate(), "yyyy-MM-dd hh:mm:ss"),
};

export function generateReportQueriesHeaders(): TableColumns<ReportQueriesEntity> {
  return {
    date: {
      renderer: DateTimeRenderer,
      width: 200,
    },
    ip: {
      renderer: StringRenderer,
      width: 125,
    },
    checkIn: {
      renderer: DateRenderer,
      width: 125,
    },
    checkOut: {
      renderer: DateRenderer,
      width: 125,
    },
    nights: {
      renderer: NumberRenderer,
      width: 125,
    },
    occupancy: {
      renderer: NumberRenderer,
      width: 125,
    },
    adults: {
      renderer: NumberRenderer,
      width: 125,
    },
    children: {
      renderer: NumberRenderer,
      width: 125,
    },
    babies: {
      renderer: NumberRenderer,
      width: 125,
    },
    locale: {
      renderer: StringRenderer,
      width: 125,
    },
    hasAvailability: {
      renderer: BooleanRenderer,
      width: 125,
    },
    isChain: {
      renderer: BooleanRenderer,
      width: 125,
    },
  };
}

export function useReportQueriesFilters(): TableFilters<
  ReportQueriesFilters,
  ReportQueriesFilters
> {
  const intl = useIntl();
  return useMemo((): TableFilters<ReportQueriesFilters, ReportQueriesFilters> => {
    return {
      period: {
        filterFn: "async",
        widget: dateFilter("period", intl),
      },
    };
  }, [intl]);
}

export function generateReportQueriesRequest({
  filterValues,
  pageSize,
  currentPage,
}): GetReportQueriesRequestProps {
  return {
    request: {
      startDate: filterValues?.period?.startDate
        ? asUTCTime(parseISO(filterValues.period.startDate), "yyyy-MM-dd")
        : defaultRequest.startDate,
      endDate: filterValues?.period?.endDate
        ? asUTCTime(parseISO(filterValues.period.endDate), "yyyy-MM-dd")
        : defaultRequest.endDate,
    },
    size: pageSize,
    page: currentPage,
  };
}

export function generateReportQueriesCsv(report: ReportQueriesEntity[], locale: string): string {
  const formattedReport: ReportQueriesCell[] = report.map(it => ({
    ...it,
    date: calculateDateTimeFromProtoDate({ value: it.date }) || "",
    checkIn: calculateDateFromProtoDate({ value: it.checkIn }) || "",
    checkOut: calculateDateFromProtoDate({ value: it.checkOut }) || "",
    hasAvailability: it.hasAvailability
      ? formatMessage(reportQueriesMessages.yes, locale)
      : formatMessage(reportQueriesMessages.no, locale),
    isChain: it.isChain
      ? formatMessage(reportQueriesMessages.yes, locale)
      : formatMessage(reportQueriesMessages.no, locale),
  }));
  const headers = generateReportQueriesHeaders();
  const cellDefinitions: ReportQueriesCellCSV[] = Object.keys(headers).map(it => ({
    message: reportQueriesMessages[it],
    value: entity => entity[it],
  }));
  const rowToCsv = (reportQueries: ReportQueriesCell) =>
    cellDefinitions.map(it => it.value(reportQueries)).join(";");

  let rows = `${cellDefinitions.map(it => formatMessage(it.message, locale)).join(";")}\n`;
  rows += formattedReport.map(it => rowToCsv(it)).join("\n");

  return URL.createObjectURL(new Blob([rows], { type: "text/csv" }));
}
